import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styles from './FrameStatsCard.module.css';
import {Card, CardActions, CardContent, Divider, IconButton, Tooltip, Typography} from "@mui/material";
import {useAppDispatch, useAppSelector} from "../../hooks";
import InputIcon from '@mui/icons-material/Input';
import InputDialog from "../InputDialog/InputDialog";
import DataPair from "../DataPair/DataPair";
import {activeVideoActions} from '../../state-slices/ActiveVideo';
import {CoordinateTransformer} from '../../state-slices/CoordinateTransformer';
import {getAppConfig} from '../../auth/GetAppConfig';
import OsGridRef from 'geodesy/osgridref';
import {GridReferenceType} from '../../state-slices/state-management/definitions/ProjectInformation';

interface FrameStatsCardProps {
}

const what3wordsMinRequestIntervalMs = 5000;

const formatNumber = (value: number, fractionDigits: number = 0) => value.toLocaleString(undefined, {
    useGrouping: false,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
});

const formatBearing = (bearing: number) => `${formatNumber(((bearing % 360) + 360) % 360, 1)}°`;
const formatLatLong = (latitude: number, longitude: number) => `${formatNumber(latitude, 6)}, ${formatNumber(longitude, 6)}`;
const formatGridPair = (east: number, north: number) => `E ${formatNumber(east)} N ${formatNumber(north)}`;
const formatEastNorthPair = (east: number, north: number) => `${formatNumber(east)}:${formatNumber(north)}`;

type LatLonPoint = { latitude: number; longitude: number };

const greatBritainPolygon: LatLonPoint[] = [
    {latitude: 50.0, longitude: -5.9},
    {latitude: 50.2, longitude: -4.1},
    {latitude: 50.6, longitude: -3.0},
    {latitude: 50.7, longitude: -1.2},
    {latitude: 51.0, longitude: 0.8},
    {latitude: 52.0, longitude: 1.8},
    {latitude: 53.4, longitude: 1.2},
    {latitude: 54.9, longitude: 0.3},
    {latitude: 56.3, longitude: -1.6},
    {latitude: 57.6, longitude: -2.6},
    {latitude: 58.6, longitude: -3.3},
    {latitude: 58.3, longitude: -5.7},
    {latitude: 57.3, longitude: -6.4},
    {latitude: 55.8, longitude: -5.8},
    {latitude: 54.8, longitude: -4.7},
    {latitude: 53.8, longitude: -3.9},
    {latitude: 52.7, longitude: -4.7},
    {latitude: 51.6, longitude: -5.3},
    {latitude: 50.7, longitude: -5.6}
];

const irelandPolygon: LatLonPoint[] = [
    {latitude: 51.4, longitude: -10.6},
    {latitude: 52.0, longitude: -10.3},
    {latitude: 53.0, longitude: -9.8},
    {latitude: 54.0, longitude: -9.4},
    {latitude: 55.2, longitude: -8.4},
    {latitude: 55.3, longitude: -7.1},
    {latitude: 54.7, longitude: -5.7},
    {latitude: 53.8, longitude: -5.5},
    {latitude: 52.8, longitude: -6.0},
    {latitude: 51.9, longitude: -6.1},
    {latitude: 51.5, longitude: -7.4},
    {latitude: 51.4, longitude: -8.8}
];

const isPointInPolygon = (point: LatLonPoint, polygon: LatLonPoint[]) => {
    let inside = false;

    for (let currentIndex = 0, previousIndex = polygon.length - 1; currentIndex < polygon.length; previousIndex = currentIndex++) {
        const current = polygon[currentIndex];
        const previous = polygon[previousIndex];

        const intersects = ((current.latitude > point.latitude) !== (previous.latitude > point.latitude))
            && (point.longitude < (previous.longitude - current.longitude) * (point.latitude - current.latitude)
                / ((previous.latitude - current.latitude) || Number.EPSILON) + current.longitude);

        if (intersects) {
            inside = !inside;
        }
    }

    return inside;
};

const shouldShowMiniMap = (point: LatLonPoint, gridReferenceType: GridReferenceType) => {
    if (!Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) {
        return false;
    }

    if (gridReferenceType === GridReferenceType.ITM) {
        return isPointInPolygon(point, irelandPolygon);
    }

    return isPointInPolygon(point, greatBritainPolygon);
};

const MiniMap: FC<{ latitude: number; longitude: number }> = ({latitude, longitude}) => {
    const latPadding = 0.0025;
    const lonPadding = 0.004;
    const bbox = [
        longitude - lonPadding,
        latitude - latPadding,
        longitude + lonPadding,
        latitude + latPadding
    ].join(',');
    const embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude},${longitude}`;
    const fullMapUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

    return <a
        className={styles.MiniMapLink}
        data-id="location-mini-map"
        href={fullMapUrl}
        target="_blank"
        rel="noreferrer"
        aria-label="Open full map"
        title="Open full map"
    >
        <div className={styles.MiniMapFrame}>
            <iframe
                className={styles.MiniMapIframe}
                src={embedUrl}
                loading="lazy"
                title="Location map"
                referrerPolicy="no-referrer-when-downgrade"
            />
            <div className={styles.MiniMapOverlay}>
                <span className={styles.MiniMapBadge}>Roads map</span>
            </div>
        </div>
    </a>;
};

interface CopyableVisualCardProps {
    className?: string;
    dataId: string;
    copyValue: string;
    copyLabel: string;
}

const CopyableVisualCard: FC<React.PropsWithChildren<CopyableVisualCardProps>> = ({children, className, dataId, copyValue, copyLabel}) => {
    const [copied, setCopied] = useState(false);

    async function copyToClipboard() {
        await window.navigator.clipboard.writeText(copyValue);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    }

    const tooltip = copied ? 'Copied' : copyLabel;

    return <Tooltip title={tooltip}>
        <div
            className={className}
            data-id={dataId}
            role="button"
            tabIndex={0}
            onClick={() => void copyToClipboard()}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    void copyToClipboard();
                }
            }}
            aria-label={copyLabel}
        >
            {children}
        </div>
    </Tooltip>;
};

const CompassDisplay: FC<{ bearing: number }> = ({bearing}) => {
    const normalizedBearing = ((bearing % 360) + 360) % 360;
    const formattedBearing = formatBearing(normalizedBearing);

    return <CopyableVisualCard
        className={`${styles.VisualCard} ${styles.CopyableVisualCard}`}
        dataId="location-compass"
        copyValue={formattedBearing}
        copyLabel="Copy bearing"
    >
        <div className={styles.Compass}>
            <div className={styles.CompassRing}/>
            <div className={styles.CompassLabelNorth}>N</div>
            <div className={styles.CompassLabelEast}>E</div>
            <div className={styles.CompassLabelSouth}>S</div>
            <div className={styles.CompassLabelWest}>W</div>
            <div className={styles.CompassNeedle} style={{transform: `translateX(-50%) rotate(${normalizedBearing}deg)`}}>
                <div className={styles.CompassNeedleTip}/>
                <div className={styles.CompassNeedleTail}/>
            </div>
            <div className={styles.CompassCenter}/>
        </div>
        <Typography variant="body2">{formattedBearing}</Typography>
    </CopyableVisualCard>;
};

const HeightIllustration: FC<{ height: number }> = ({height}) => {
    const minimumHeight = Math.min(0, Math.floor((height - 20) / 25) * 25);
    const maximumHeight = Math.max(100, Math.ceil((height + 20) / 25) * 25);
    const range = Math.max(maximumHeight - minimumHeight, 1);
    const markerBottom = ((height - minimumHeight) / range) * 100;
    const seaLevelBottom = ((0 - minimumHeight) / range) * 100;
    const formattedHeight = `${formatNumber(height, 1)} m`;

    return <CopyableVisualCard
        className={`${styles.VisualCard} ${styles.CopyableVisualCard}`}
        dataId="height-illustration"
        copyValue={formattedHeight}
        copyLabel="Copy height"
    >
        <div className={styles.HeightScale}>
            <div className={styles.HeightTrack}/>
            <div className={styles.SeaLevelLine} style={{bottom: `${seaLevelBottom}%`}}/>
            <div className={styles.HeightMarker} style={{bottom: `${markerBottom}%`}}>
                <span className={styles.HeightMarkerLabel}>{formattedHeight}</span>
            </div>
            <span className={styles.HeightMaxLabel}>{formatNumber(maximumHeight)} m</span>
            <span className={styles.HeightMinLabel}>{formatNumber(minimumHeight)} m</span>
        </div>
        <Typography variant="body2">{formattedHeight}</Typography>
    </CopyableVisualCard>;
};

const FrameStatsCard: FC<FrameStatsCardProps> = () => {
    const frame = useAppSelector(state => state.activeVideoSlice.currentFrame);
    const loc = useAppSelector(state => state.activeVideoSlice.locationState.nearestLoc);
    const gps = useAppSelector(state => state.activeVideoSlice.locationState.nearestGps);
    const isPlaying = useAppSelector(state => state.activeVideoSlice.videoIsPlaying);
    const currentProject = useAppSelector(state => state.activeVideoSlice.projectState.currentProject);
    const dispatch = useAppDispatch();
    const what3wordsApiKey = getAppConfig().what3wordsApiKey;

    const metreFormatted = loc?.metre?.toLocaleString(undefined, {
        useGrouping: false,
        minimumFractionDigits: 1
    });
    const metre = loc?.metre || 0;

    const [goToFrameDialogOpen, setFrameDialogOpen] = useState(false);
    const [goToMetreDialogOpen, setMetreDialogOpen] = useState(false);
    const [what3words, setWhat3words] = useState<string | undefined>(undefined);
    const latestWgs84CoordinatesRef = useRef<{latitude: number; longitude: number} | undefined>(undefined);

    const wgs84Coordinates = useMemo(() => {
        if (!gps || !currentProject) {
            return undefined;
        }

        const [latitude, longitude] = CoordinateTransformer.convertProjectGridToWgs84(
            gps.east,
            gps.north,
            currentProject.gridReferenceType
        );

        return {latitude, longitude};
    }, [currentProject, gps]);

    const utmReference = wgs84Coordinates
        ? CoordinateTransformer.convertWgs84ToUtm(wgs84Coordinates.latitude, wgs84Coordinates.longitude)
        : undefined;
    const itmCoordinates = wgs84Coordinates
        ? CoordinateTransformer.convertWgs84ToItm(wgs84Coordinates.latitude, wgs84Coordinates.longitude)
        : undefined;
    const irishGridCoordinates = wgs84Coordinates
        ? CoordinateTransformer.convertWgs84ToIrishGrid(wgs84Coordinates.latitude, wgs84Coordinates.longitude)
        : undefined;
    const osgbReference = gps && currentProject?.gridReferenceType === GridReferenceType.OSGB
        ? new OsGridRef(gps.east, gps.north).toString(10)
        : undefined;
    const isNiProject = currentProject?.gridReferenceType === GridReferenceType.ITM;
    const isUkProject = currentProject?.gridReferenceType === GridReferenceType.OSGB;
    const showMiniMap = Boolean(wgs84Coordinates && currentProject && shouldShowMiniMap(wgs84Coordinates, currentProject.gridReferenceType));
    const hasWgs84Coordinates = Boolean(wgs84Coordinates);

    useEffect(() => {
        latestWgs84CoordinatesRef.current = wgs84Coordinates;
        if (!wgs84Coordinates) {
            setWhat3words(undefined);
        }
    }, [wgs84Coordinates]);

    useEffect(() => {
        if (!what3wordsApiKey) {
            setWhat3words(undefined);
            return;
        }

        let activeAbortController: AbortController | undefined;

        const fetchWhat3words = async () => {
            const currentCoordinates = latestWgs84CoordinatesRef.current;
            if (!gps || !currentCoordinates) {
                setWhat3words(undefined);
                return;
            }

            activeAbortController?.abort();
            activeAbortController = new AbortController();

            try {
                const response = await fetch(
                    `https://api.what3words.com/v3/convert-to-3wa?coordinates=${currentCoordinates.latitude},${currentCoordinates.longitude}&key=${what3wordsApiKey}&language=en`,
                    {signal: activeAbortController.signal}
                );

                if (!response.ok) {
                    setWhat3words(undefined);
                    return;
                }

                const payload = await response.json() as {words?: string};
                setWhat3words(payload.words);
            } catch (error) {
                if ((error as {name?: string})?.name !== 'AbortError') {
                    setWhat3words(undefined);
                }
            }
        };

        void fetchWhat3words();
        const intervalId = window.setInterval(() => {
            void fetchWhat3words();
        }, what3wordsMinRequestIntervalMs);

        return () => {
            activeAbortController?.abort();
            window.clearInterval(intervalId);
        };
    }, [gps, hasWgs84Coordinates, what3wordsApiKey]);

    function openGoToFrameDialog() {
        setFrameDialogOpen(true);
    }

    function goToFrame(nextFrame: number) {
        setFrameDialogOpen(false);
        dispatch(activeVideoActions.goDirectToFrame(nextFrame));
    }

    function openGoToMetreDialog() {
        setMetreDialogOpen(true);
    }

    function goToMetre(meterage: number) {
        setMetreDialogOpen(false);
        dispatch(activeVideoActions.goDirectToMeterage(meterage));
    }

    return <div className={styles.FrameStatsCard}>
        <Card>
            <CardContent>
                <DataPair dataId="frame-value" value={frame} label="Frame">
                    <span>
                        <Tooltip title="Go to frame">
                            <IconButton disabled={isPlaying}
                                        data-id="go-to-frame"
                                        onClick={() => openGoToFrameDialog()}
                                        size="small" color="primary" aria-label="Go to specific frame"
                                        className={styles.GoToButton}>
                                <InputIcon/>
                            </IconButton>
                        </Tooltip>
                    </span>
                </DataPair>
                <Divider/>
                {loc && <>
                    <DataPair dataId="meterage" value={metreFormatted} label="Meterage" copyValue={metreFormatted} copyLabel="Copy meterage">
                        <span>
                            <Tooltip title="Go to meterage">
                                <IconButton disabled={isPlaying}
                                            data-id="go-to-meterage"
                                            size="small" color="primary"
                                            aria-label="Go to specific meterage"
                                            onClick={() => openGoToMetreDialog()}
                                            className={styles.GoToButton}>
                                    <InputIcon/>
                                </IconButton>
                            </Tooltip>
                        </span>
                    </DataPair>
                    <Divider/>
                </>}
                {gps && <>
                    <DataPair
                        dataId="east-north-value"
                        value={formatEastNorthPair(gps.east, gps.north)}
                        label="E:N"
                        copyValue={formatEastNorthPair(gps.east, gps.north)}
                        copyLabel="Copy east and north"
                    />
                    <Divider/>
                    {wgs84Coordinates && <>
                        <DataPair dataId="lat-long-value" value={formatLatLong(wgs84Coordinates.latitude, wgs84Coordinates.longitude)} label="Lat / Long" copyValue={formatLatLong(wgs84Coordinates.latitude, wgs84Coordinates.longitude)} copyLabel="Copy latitude and longitude"/>
                        <Divider/>
                    </>}
                    {isNiProject && irishGridCoordinates && <>
                        <DataPair
                            dataId="irish-grid-value"
                            value={formatGridPair(irishGridCoordinates[0], irishGridCoordinates[1])}
                            label="Irish Grid"
                            copyValue={formatGridPair(irishGridCoordinates[0], irishGridCoordinates[1])}
                            copyLabel="Copy Irish Grid reference"
                        />
                        <Divider/>
                    </>}
                    {isUkProject && osgbReference && <>
                        <DataPair
                            dataId="osgb-ref-value"
                            value={osgbReference}
                            label="OSGB Ref"
                            copyValue={osgbReference}
                            copyLabel="Copy OSGB reference"
                        />
                        <Divider/>
                    </>}
                    {utmReference && <>
                        <DataPair dataId="utm-value" value={utmReference} label="UTM Ref" copyValue={utmReference} copyLabel="Copy UTM reference"/>
                        <Divider/>
                    </>}
                    {isNiProject && itmCoordinates && <>
                        <DataPair
                            dataId="itm-ref-value"
                            value={formatGridPair(itmCoordinates[0], itmCoordinates[1])}
                            label="ITM Ref"
                            copyValue={formatGridPair(itmCoordinates[0], itmCoordinates[1])}
                            copyLabel="Copy ITM reference"
                        />
                        <Divider/>
                    </>}
                    {what3words && <>
                        <DataPair dataId="what3words-value" value={what3words} label="what3words" copyValue={what3words} copyLabel="Copy what3words"/>
                        <Divider/>
                    </>}
                    {showMiniMap && wgs84Coordinates && <>
                        <MiniMap latitude={wgs84Coordinates.latitude} longitude={wgs84Coordinates.longitude}/>
                        <Divider/>
                    </>}
                    <div className={styles.Visuals}>
                        <CompassDisplay bearing={gps.bearing}/>
                        <HeightIllustration height={gps.height}/>
                    </div>
                    <Divider/>
                </>}
            </CardContent>
            <CardActions>
            </CardActions>
        </Card>

        <InputDialog isOpen={goToFrameDialogOpen} data-id="go-to-frame-dialog" initialValue={frame} fieldLabel="Frame" prompt={"Go to frame"} onClose={nextFrame => goToFrame(nextFrame)}/>
        <InputDialog isOpen={goToMetreDialogOpen} data-id="go-to-meterage-dialog" initialValue={metre} fieldLabel="Meterage" prompt={"Go to meterage"} onClose={meterage => goToMetre(meterage)}/>
    </div>;
};

export default FrameStatsCard;
